// EP-136 T136-023 — the real Book-unit form, replacing the T136-022
// placeholder shell. POSTs via useCreateBooking (POST /api/units/:unitId/booking,
// backed by UnitBookingService).
//
// The booking MODE (deposit vs revenue) is resolved entirely server-side from
// Property#resolved_booking_settings and decides the GL account + refundability
// — the endpoint only accepts customer_id/started_at/amount/reservation_expires_at,
// so a mode toggle in this modal would be a phantom control: the operator would
// pick a mode, the booking would be created in whatever the property says, and
// the two could disagree. The resolved mode is shown READ-ONLY, with a link to
// the property's Booking settings (T136-026, Overview tab) for the operator to
// change it there instead.
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Modal, Button, Input, Alert, Badge, SearchableDropdown, PreFilledBadge } from '@/components/ui'
import { useCreateBooking } from '@/hooks/useUnits'
import { useCustomerSearch } from '@/hooks/useCustomers'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useNotificationStore } from '@/stores'
import { localToday } from '@/lib/date'
import { resolveBookingSettingsFormDefaults } from '@/lib/bookingSettings'
import type { Property } from '@/types'

export interface BookingModalProps {
  isOpen: boolean
  onClose: () => void
  unitId: number
  /**
   * The unit's property — supplies `booking_settings` for the resolved-mode
   * display and the amount default. Undefined (property still loading) is
   * handled gracefully: the resolution falls back to the account-level
   * default only, same as an unset property override.
   */
  property?: Property
}

interface BookingFormData {
  customer_id: string
  started_at: string
  amount: number | undefined
  reservation_expires_at: string
}

/**
 * Mirrors `UnitBookingService#default_reservation_expires_at`
 * (`Time.current.utc.end_of_month`, api/app/services/unit_booking_service.rb)
 * so the date field can start with a sensible, editable value instead of
 * blank. This is a plain calendar-day computation, not pricing math — the
 * "no calculation in the frontend" rule is about money (qty×price, caps,
 * remainders), not calendar arithmetic, and this value is never used to
 * derive an amount. If the operator clears the field, it is omitted from
 * the payload and the server computes its own default at booking time.
 */
function defaultReservationExpiresAt(): string {
  const now = new Date()
  const lastDayOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0))
  const yyyy = lastDayOfMonth.getUTCFullYear()
  const mm = String(lastDayOfMonth.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(lastDayOfMonth.getUTCDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function BookingModal({ isOpen, onClose, unitId, property }: BookingModalProps) {
  const { t } = useTranslation('units')
  const { t: tc } = useTranslation('common')
  const { t: tp } = useTranslation('properties')
  const translateError = useApiErrorTranslator('units')
  const addToast = useNotificationStore((s) => s.addToast)
  const { timezone } = useLocalizationConfig()

  const { data: accountSettings } = useAccountSettings()
  const createBooking = useCreateBooking()

  const [customerSearch, setCustomerSearch] = useState('')
  // fetchOnEmpty tied to isOpen: the query is a plain useQuery (always
  // "mounted" since Modal renders its children regardless of isOpen), so
  // gating fetchOnEmpty this way is what stops an empty-string customer
  // fetch from firing on every UnitDetailPage load.
  const { data: searchResults, isLoading: isLoadingCustomers } = useCustomerSearch(customerSearch, {
    fetchOnEmpty: isOpen,
  })

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<BookingFormData>({
    defaultValues: {
      customer_id: '',
      started_at: '',
      amount: undefined,
      reservation_expires_at: '',
    },
  })

  // Resolved PER KEY (this property's override, else the account default,
  // else the hardcoded fallback) — reuses the exact same helper the
  // property form's Booking section uses to build its "inherited vs
  // overridden" UI (src/lib/bookingSettings.ts), so this modal's read of
  // Property#resolved_booking_settings can't drift into a second rule.
  const resolved = resolveBookingSettingsFormDefaults(
    property?.booking_settings,
    accountSettings?.settings?.booking_settings,
  )

  // Reset the form fresh every time the modal opens (RHF reset is not React
  // state — lint-legal; mirrors TransferOwnershipModal's same pattern).
  useEffect(() => {
    if (isOpen) {
      reset({
        customer_id: '',
        started_at: localToday(timezone),
        amount: resolved.booking_fee_default,
        reservation_expires_at: defaultReservationExpiresAt(),
      })
      setCustomerSearch('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, timezone])

  const handleClose = () => {
    setCustomerSearch('')
    onClose()
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      await createBooking.mutateAsync({
        unitId,
        customer_id: Number(data.customer_id),
        started_at: data.started_at,
        // amount / reservation_expires_at are optional on the endpoint — an
        // unset or cleared field is OMITTED so the server applies its own
        // resolved default, rather than sending NaN or an empty string.
        ...(data.amount !== undefined && !Number.isNaN(data.amount) ? { amount: data.amount } : {}),
        ...(data.reservation_expires_at ? { reservation_expires_at: data.reservation_expires_at } : {}),
      })
      addToast({ type: 'success', message: t('booking.messages.create_success') })
      handleClose()
    } catch (err) {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code
      const message = translateError(err)
      // amount_required / amount_invalid / amount_exceeds_deposit are about
      // the Amount field specifically — surface them there instead of a
      // generic toast. The other three typed codes (condominium-account,
      // unit_occupied, unit_already_reserved) are about the unit/account as
      // a whole, not any one field, so they stay a toast.
      if (code === 'amount_required' || code === 'amount_invalid' || code === 'amount_exceeds_deposit') {
        setError('amount', { type: 'server', message })
      } else {
        addToast({ type: 'error', message })
      }
    }
  })

  const customerOptions = (searchResults?.data ?? []).map((c) => ({
    value: String(c.id),
    label: c.name,
  }))

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('booking.modal_title')}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={createBooking.isPending}>
            {tc('actions.cancel')}
          </Button>
          <Button onClick={onSubmit} loading={createBooking.isPending} data-testid="confirm-booking">
            {t('actions.book')}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Controller
          control={control}
          name="customer_id"
          rules={{ required: tc('errors.required') }}
          render={({ field }) => (
            <SearchableDropdown
              name="customer_id"
              label={t('booking.form.customer_label')}
              placeholder={t('booking.form.customer_placeholder')}
              required
              options={customerOptions}
              value={field.value}
              onChange={field.onChange}
              onSearch={setCustomerSearch}
              isLoading={isLoadingCustomers}
              error={errors.customer_id?.message}
            />
          )}
        />

        <Input
          type="date"
          label={t('booking.form.started_at_label')}
          hint={t('booking.form.started_at_hint')}
          required
          {...register('started_at', { required: tc('errors.required') })}
          error={errors.started_at?.message}
        />

        {/* Read-only resolved mode. See the file banner — never a form control. */}
        <Alert variant="info" data-testid="booking-resolved-mode">
          <div className="flex items-center gap-2 flex-wrap">
            <span>{t('booking.form.mode_label')}</span>
            <Badge variant={resolved.booking_fee_mode === 'deposit' ? 'info' : 'ghost'}>
              {tp(`booking.fee_mode_options.${resolved.booking_fee_mode}`)}
            </Badge>
          </div>
          <p className="mt-1">
            {t('booking.form.mode_hint')}
            {property && (
              <Link to={`/properties/${property.id}`} className="text-primary hover:underline ml-1">
                {t('booking.form.mode_hint_link')}
              </Link>
            )}
          </p>
        </Alert>

        <div>
          <label className="label" htmlFor="amount">
            <span className="label-text flex items-center gap-1">
              {t('booking.form.amount_label')}
              {resolved.booking_fee_default !== undefined && <PreFilledBadge className="ml-1" />}
            </span>
          </label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            min="0.01"
            leftIcon={<span className="text-sm font-medium">฿</span>}
            hint={resolved.booking_fee_default === undefined ? tp('booking.fee_default_not_set') : undefined}
            {...register('amount', { valueAsNumber: true })}
            error={errors.amount?.message}
          />
        </div>

        <div>
          <label className="label" htmlFor="reservation_expires_at">
            <span className="label-text flex items-center gap-1">
              {t('booking.form.reservation_expires_at_label')}
              <PreFilledBadge className="ml-1" />
            </span>
          </label>
          <Input
            id="reservation_expires_at"
            type="date"
            hint={t('booking.form.reservation_expires_at_hint')}
            {...register('reservation_expires_at')}
            error={errors.reservation_expires_at?.message}
          />
        </div>
      </form>
    </Modal>
  )
}
